/**
 * Auth → sidebar chain recorder.
 *
 * Always keeps the latest sample in sessionStorage (`bb-perf-shell-last`) and
 * on `window.__bbPerfShell`. History is opt-in:
 *
 *   localStorage.setItem('bb-perf-shell', '1'); location.reload();
 *   copy(JSON.stringify(window.__bbPerfShell.rows(), null, 2))
 *
 * Slow samples also emit a Vercel Analytics custom event (`staff_shell_slow`).
 * Thresholds are alert lines, not measured baselines.
 *
 * Cross-document login timing uses wall-clock stamps because a hard navigation
 * resets performance.now(). In-document marks use performance.now().
 */

export const SHELL_SLOW_INTERACTIVE_MS = 8000;
export const SHELL_SLOW_SERVER_MS = 5000;
const DUP_WINDOW_MS = 10_000;
const LAST_KEY = 'bb-perf-shell-last';
const HISTORY_FLAG = 'bb-perf-shell';
const HISTORY_KEY = 'bb-perf-shell-rows';
const LOGIN_KEY = 'bb-perf-login';
const MAX_HISTORY = 40;

export type ShellSample = {
  at: string;
  role: string | null;
  isSuperuser: boolean;
  title: string | null;
  cacheHit: boolean;
  serverMs: number | null;
  coldStart: boolean | null;
  isolateAgeMs: number | null;
  serverCallIndex: number | null;
  authProfileMs: number | null;
  criticalPaintMs: number | null;
  interactiveNavMs: number | null;
  loginActionMs: number | null;
  loginToInteractiveMs: number | null;
  fcpMs: number | null;
  lcpMs: number | null;
  cls: number | null;
  navigationType: string;
  duplicateShellCalls: number;
  actions: { name: string; duplicateWithin10s: number; retry: boolean }[];
};

type ActionNote = { name: string; at: number; retry: boolean };

type LoginStamp = { submittedAt: number; actionMs: number; role?: string };

const actionLog: ActionNote[] = [];
let authProfileMs: number | null = null;
let authRole: string | null = null;
let criticalPaintMs: number | null = null;
let latestLcp: number | null = null;
let latestCls = 0;
let observersInstalled = false;
let finalized = false;

export function countRecentDuplicates(
  log: { name: string; at: number; retry?: boolean }[],
  name: string,
  now: number,
  windowMs = DUP_WINDOW_MS,
): number {
  return log.filter(entry =>
    entry.name === name
    && !entry.retry
    && now - entry.at >= 0
    && now - entry.at < windowMs
  ).length;
}

export function noteAction(name: string, opts?: { retry?: boolean }): number {
  const now = Date.now();
  const prior = countRecentDuplicates(actionLog, name, now);
  actionLog.push({ name, at: now, retry: !!opts?.retry });
  if (actionLog.length > 50) actionLog.splice(0, actionLog.length - 50);
  return opts?.retry ? 0 : prior;
}

export function stampLoginSuccess(stamp: LoginStamp) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(LOGIN_KEY, JSON.stringify(stamp));
  } catch { /* ignore */ }
}

export function markAuthProfile(role: string | null) {
  if (authProfileMs != null || typeof performance === 'undefined') return;
  authProfileMs = Math.round(performance.now());
  authRole = role;
  installShellGlobals();
}

export function markNavPhase(phase: 'critical' | 'interactive') {
  if (typeof performance === 'undefined') return;
  ensurePaintObservers();
  if (phase === 'critical') {
    if (criticalPaintMs == null) criticalPaintMs = Math.round(performance.now());
    return;
  }
  if (finalized) return;
  const gen = sampleGen;
  // Layout shift from gated links is recorded on the frame after commit.
  window.setTimeout(() => {
    if (gen !== sampleGen || finalized) return;
    finalizeShellSample();
  }, 50);
}

export type ServerTimingInput = {
  serverMs: number;
  coldStart: boolean;
  isolateAgeMs: number;
  callIndex: number;
  cacheHit: boolean;
  role: string | null;
  isSuperuser: boolean;
  title: string | null;
};

let pendingServer: ServerTimingInput | null = null;
let paintedFromCache = false;
let sampleGen = 0;

/** Drop an in-flight sample so the next staff entry in this tab can record again.
 *  Keeps the document's first critical-paint mark; child effects often record it
 *  before this provider effect runs.
 */
export function reopenShellSample() {
  sampleGen += 1;
  finalized = false;
  pendingServer = null;
  paintedFromCache = false;
}

export function noteShellServer(timing: ServerTimingInput) {
  if (timing.cacheHit) paintedFromCache = true;
  const merged: ServerTimingInput = { ...timing, cacheHit: paintedFromCache || timing.cacheHit };
  // A cache note must not wipe server timings that already arrived.
  if (timing.cacheHit && pendingServer && pendingServer.serverMs > 0) {
    pendingServer = { ...pendingServer, cacheHit: true };
  } else {
    pendingServer = merged;
  }
  installShellGlobals();
  if (finalized && pendingServer.serverMs > 0) patchLastSample(pendingServer);
  // Cold starts are recorded on the sample. Alert only when the action itself is slow
  // so a healthy first request after deploy does not page anyone.
  if (timing.serverMs >= SHELL_SLOW_SERVER_MS) {
    void reportSlow('server', {
      serverMs: timing.serverMs,
      coldStart: timing.coldStart,
      isolateAgeMs: timing.isolateAgeMs,
      callIndex: timing.callIndex,
      cacheHit: pendingServer.cacheHit,
      role: timing.role ?? 'unknown',
    });
  }
}

export function markStudentShellSkipped(role: string | null) {
  if (finalized) return;
  markAuthProfile(role);
  pendingServer = {
    serverMs: 0,
    coldStart: false,
    isolateAgeMs: 0,
    callIndex: 0,
    cacheHit: false,
    role,
    isSuperuser: false,
    title: null,
  };
  markNavPhase('interactive');
}

function readLoginStamp(): LoginStamp | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(LOGIN_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(LOGIN_KEY);
    const parsed = JSON.parse(raw) as LoginStamp;
    if (typeof parsed?.submittedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function navigationType(): string {
  if (typeof performance === 'undefined') return 'unknown';
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return nav?.type ?? 'unknown';
}

function readFcp(): number | null {
  if (typeof performance === 'undefined') return null;
  const paint = performance.getEntriesByType('paint').find(entry => entry.name === 'first-contentful-paint');
  return paint ? Math.round(paint.startTime) : null;
}

function finalizeShellSample() {
  if (finalized) return;
  finalized = true;
  const login = readLoginStamp();
  const now = Date.now();
  const server = pendingServer;
  const interactiveNavMs = typeof performance === 'undefined' ? null : Math.round(performance.now());
  const recentShellStarts = actionLog.filter(entry =>
    entry.name === 'getMyStaffShell' && !entry.retry && now - entry.at < DUP_WINDOW_MS
  );
  const duplicateShellCalls = Math.max(0, recentShellStarts.length - 1);
  const sample: ShellSample = {
    at: new Date().toISOString(),
    role: server?.role ?? authRole,
    isSuperuser: server?.isSuperuser ?? false,
    title: server?.title ?? null,
    cacheHit: server?.cacheHit ?? false,
    serverMs: server?.serverMs ?? null,
    coldStart: server?.coldStart ?? null,
    isolateAgeMs: server?.isolateAgeMs ?? null,
    serverCallIndex: server?.callIndex ?? null,
    authProfileMs,
    criticalPaintMs,
    interactiveNavMs,
    loginActionMs: login?.actionMs ?? null,
    loginToInteractiveMs: login ? Math.max(0, now - login.submittedAt) : null,
    fcpMs: readFcp(),
    lcpMs: latestLcp == null ? null : Math.round(latestLcp),
    cls: Math.round(latestCls * 1000) / 1000,
    navigationType: navigationType(),
    duplicateShellCalls,
    actions: actionLog.map((entry, index) => ({
      name: entry.name,
      retry: entry.retry,
      duplicateWithin10s: countRecentDuplicates(actionLog.slice(0, index), entry.name, entry.at),
    })),
  };
  persistSample(sample);
  if (
    (sample.interactiveNavMs != null && sample.interactiveNavMs >= SHELL_SLOW_INTERACTIVE_MS)
    || duplicateShellCalls > 0
  ) {
    void reportSlow('interactive', {
      interactiveNavMs: sample.interactiveNavMs ?? -1,
      criticalPaintMs: sample.criticalPaintMs ?? -1,
      serverMs: sample.serverMs ?? -1,
      coldStart: sample.coldStart === true,
      cacheHit: sample.cacheHit,
      duplicateShellCalls,
      role: sample.role ?? 'unknown',
      isSuperuser: sample.isSuperuser,
      navigationType: sample.navigationType,
      fcpMs: sample.fcpMs ?? -1,
      lcpMs: sample.lcpMs ?? -1,
      cls: sample.cls ?? -1,
    });
  }
}

function patchLastSample(server: ServerTimingInput) {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(LAST_KEY);
    if (!raw) return;
    const last = JSON.parse(raw) as ShellSample;
    const patched: ShellSample = {
      ...last,
      cacheHit: last.cacheHit || server.cacheHit,
      serverMs: server.serverMs,
      coldStart: server.coldStart,
      isolateAgeMs: server.isolateAgeMs,
      serverCallIndex: server.callIndex,
      role: server.role ?? last.role,
      isSuperuser: server.isSuperuser,
      title: server.title,
    };
    sessionStorage.setItem(LAST_KEY, JSON.stringify(patched));
  } catch { /* ignore */ }
}

/** New login in the same tab should record a fresh chain. */
export function resetShellRecorder() {
  actionLog.length = 0;
  authProfileMs = null;
  authRole = null;
  criticalPaintMs = null;
  latestLcp = null;
  latestCls = 0;
  finalized = false;
  pendingServer = null;
  paintedFromCache = false;
  sampleGen += 1;
}

function persistSample(sample: ShellSample) {
  installShellGlobals();
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(LAST_KEY, JSON.stringify(sample));
  } catch { /* ignore */ }
  if (!historyEnabled()) return;
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const prev = raw ? JSON.parse(raw) as ShellSample[] : [];
    const next = [...(Array.isArray(prev) ? prev : []), sample].slice(-MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

function historyEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(HISTORY_FLAG) === '1';
  } catch {
    return false;
  }
}

function readHistory(): ShellSample[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readLast(): ShellSample | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(LAST_KEY);
    return raw ? JSON.parse(raw) as ShellSample : null;
  } catch {
    return null;
  }
}

export function installShellGlobals() {
  if (typeof window === 'undefined') return;
  ensurePaintObservers();
  window.__bbPerfShell = {
    last: readLast,
    rows: readHistory,
    clear: () => {
      try {
        sessionStorage.removeItem(LAST_KEY);
        localStorage.removeItem(HISTORY_KEY);
      } catch { /* ignore */ }
    },
    thresholds: {
      interactiveMs: SHELL_SLOW_INTERACTIVE_MS,
      serverMs: SHELL_SLOW_SERVER_MS,
    },
  };
}

function ensurePaintObservers() {
  if (observersInstalled || typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return;
  observersInstalled = true;
  try {
    const lcp = new PerformanceObserver(list => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) latestLcp = last.startTime;
    });
    lcp.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch { /* unsupported */ }
  try {
    const cls = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        const shift = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
        if (!shift.hadRecentInput && typeof shift.value === 'number') latestCls += shift.value;
      }
    });
    cls.observe({ type: 'layout-shift', buffered: true });
  } catch { /* unsupported */ }
}

async function reportSlow(kind: 'server' | 'interactive', props: Record<string, string | number | boolean>) {
  try {
    const { track } = await import('@vercel/analytics');
    track('staff_shell_slow', { kind, ...props });
  } catch {
    /* Analytics is optional; the sessionStorage sample still exists. */
  }
}

declare global {
  interface Window {
    __bbPerfShell?: {
      last: () => ShellSample | null;
      rows: () => ShellSample[];
      clear: () => void;
      thresholds: { interactiveMs: number; serverMs: number };
    };
  }
}
